// components/AddTypeForm.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useAuthContext } from "../context/AuthContext";
import { usePositions } from "../hooks/usePositions";
import {
  getTypeQuickReplies,
  createTypeQuickReply,
  updateTypeQuickReply,
  deleteTypeQuickReply,
} from "../api/client";

const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");
const n = (v) => (v === "" || v == null ? null : Number(v));

const AddTypeForm = ({ typeData, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();

  // Cargos para permissão
  const { data: posData } = usePositions(1, 500, "");
  const positions = useMemo(
    () => posData?.positions || posData?.data || [],
    [posData]
  );

  const [errorMessage, setErrorMessage] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [quickRepliesLoading, setQuickRepliesLoading] = useState(false);
  const [quickRepliesError, setQuickRepliesError] = useState(null);
  const [quickReplyDialogOpen, setQuickReplyDialogOpen] = useState(false);
  const [editingQuickReply, setEditingQuickReply] = useState(null);
  const [quickReplyMensagem, setQuickReplyMensagem] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [formData, setFormData] = useState({
    ...typeData,
    // status vem como 0/1
    status: typeData?.status == null ? 1 : Number(typeData.status),
    ordem: typeData?.ordem ?? "",
    id_usuario: typeData?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    allowed_positions: Array.isArray(typeData?.allowed_positions)
      ? typeData.allowed_positions
      : [],

    // ✅ campos pedidos
    // tipo (0 = Comercial, 1 = Relacionamento) — usa o que veio do banco
    tipo: typeData?.tipo == null ? 0 : Number(typeData.tipo),
    // default (0/1)
    default: typeData?.default == null ? 0 : Number(typeData.default),
  });

  // Quando trocar de tipo (edição), recarregar dados de forma segura
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...typeData,
      status: typeData?.status == null ? 1 : Number(typeData.status),
      ordem: typeData?.ordem ?? "",
      id_usuario: typeData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      allowed_positions: Array.isArray(typeData?.allowed_positions)
        ? typeData.allowed_positions
        : [],
      // ✅ garantir que o select reflita o valor do banco
      tipo: typeData?.tipo == null ? 0 : Number(typeData.tipo),
      default: typeData?.default == null ? 0 : Number(typeData.default),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeData?.id]);

  const typeId = formData.id || typeData?.id;

  useEffect(() => {
    if (!typeId) {
      setQuickReplies([]);
      return;
    }
    let cancelled = false;
    setQuickRepliesLoading(true);
    setQuickRepliesError(null);
    getTypeQuickReplies(typeId)
      .then(({ items }) => {
        if (!cancelled) setQuickReplies(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setQuickRepliesError(err?.message || "Erro ao carregar respostas.");
          setQuickReplies([]);
        }
      })
      .finally(() => {
        if (!cancelled) setQuickRepliesLoading(false);
      });
    return () => { cancelled = true; };
  }, [typeId]);

  const openAddQuickReply = () => {
    setEditingQuickReply(null);
    setQuickReplyMensagem("");
    setQuickReplyDialogOpen(true);
  };

  const openEditQuickReply = (item) => {
    setEditingQuickReply(item);
    setQuickReplyMensagem(item.mensagem || "");
    setQuickReplyDialogOpen(true);
  };

  const closeQuickReplyDialog = () => {
    setQuickReplyDialogOpen(false);
    setEditingQuickReply(null);
    setQuickReplyMensagem("");
  };

  const saveQuickReply = async () => {
    const msg = (quickReplyMensagem || "").trim();
    if (!msg) return;
    if (!typeId) return;
    setQuickRepliesError(null);
    try {
      if (editingQuickReply?.id) {
        await updateTypeQuickReply(typeId, editingQuickReply.id, { mensagem: msg });
        setQuickReplies((prev) =>
          prev.map((r) => (r.id === editingQuickReply.id ? { ...r, mensagem: msg } : r))
        );
      } else {
        const created = await createTypeQuickReply(typeId, { mensagem: msg });
        setQuickReplies((prev) => [...prev, { id: created?.id, mensagem: msg, id_tipolead: typeId }]);
      }
      closeQuickReplyDialog();
    } catch (err) {
      setQuickRepliesError(err?.response?.data?.message || err?.message || "Erro ao salvar.");
    }
  };

  const handleDeleteQuickReply = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDeleteQuickReply = async () => {
    if (!deleteConfirmId || !typeId) return;
    try {
      await deleteTypeQuickReply(typeId, deleteConfirmId);
      setQuickReplies((prev) => prev.filter((r) => r.id !== deleteConfirmId));
      setDeleteConfirmId(null);
    } catch (err) {
      setQuickRepliesError(err?.response?.data?.message || err?.message || "Erro ao excluir.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // converter 0/1 quando necessário
    if (["status", "tipo", "default"].includes(name)) {
      setFormData((p) => ({ ...p, [name]: Number(value) }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    const payload = {
      id: formData.id,
      nome: (formData.nome || "").trim(),
      descricao: formData.descricao || "",
      ordem: n(digitsOnly(formData.ordem)),
      status: Number(formData.status) === 1 ? 1 : 0,
      id_usuario: formData.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,

      // ✅ novos campos exatamente como solicitado
      tipo: Number(formData.tipo) === 1 ? 1 : 0, // 0=Comercial, 1=Relacionamento
      default: Number(formData.default) === 1 ? 1 : 0, // 0=Não default, 1=Default

      // cargos permitidos
      allowed_positions: Array.isArray(formData.allowed_positions)
        ? formData.allowed_positions
        : [],
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Erro ao salvar o tipo."
      );
    }
  };

  // opções do Autocomplete
  const positionOptions = positions.map((p) => ({
    id: p.id,
    label: p.nome || p.name || `#${p.id}`,
  }));
  const valuePositions = positionOptions.filter((opt) =>
    (formData.allowed_positions || []).includes(opt.id)
  );

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {typeData?.id ? "Editar Tipo" : "Adicionar Tipo"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Nome"
              name="nome"
              value={formData.nome || ""}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Ordem"
              name="ordem"
              value={formData.ordem ?? ""}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  ordem: digitsOnly(e.target.value),
                }))
              }
              inputMode="numeric"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              name="descricao"
              value={formData.descricao || ""}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          {/* ✅ Comercial / Relacionamento (usa `tipo` do backend) */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="tipo-label">Tipo de atendimento</InputLabel>
              <Select
                labelId="tipo-label"
                label="Tipo de atendimento"
                name="tipo"
                value={Number(formData.tipo) || 0}
                onChange={handleChange}
              >
                <MenuItem value={0}>Comercial</MenuItem>
                <MenuItem value={1}>Relacionamento</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* ✅ Default (0/1) */}
          <Grid item xs={12} md={6} alignItems="center" display="flex">
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.default) === 1}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      default: e.target.checked ? 1 : 0,
                    }))
                  }
                />
              }
              label={
                Number(formData.default) === 1
                  ? "Tipo default"
                  : "Não é default"
              }
            />
          </Grid>

          {/* Cargos permitidos */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={positionOptions}
              value={valuePositions}
              onChange={(_, newValue) =>
                setFormData((p) => ({
                  ...p,
                  allowed_positions: newValue.map((v) => v.id),
                }))
              }
              disableCloseOnSelect
              getOptionLabel={(opt) => opt.label}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.label}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cargos com permissão"
                  placeholder="Selecione cargos"
                />
              )}
            />
          </Grid>

          {/* Respostas pré-definidas (apenas ao editar tipo existente) */}
          {typeId && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Respostas pré-definidas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Mensagens rápidas exibidas no chat para este tipo de lead.
              </Typography>
              {quickRepliesError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setQuickRepliesError(null)}>
                  {quickRepliesError}
                </Alert>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={openAddQuickReply}
                >
                  Adicionar resposta
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ maxHeight: 280, overflow: "auto" }}>
                {quickRepliesLoading ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : quickReplies.length === 0 ? (
                  <Box sx={{ p: 2, color: "text.secondary", fontSize: "0.875rem" }}>
                    Nenhuma resposta pré-definida. Clique em &quot;Adicionar resposta&quot; para incluir.
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {quickReplies.map((item) => (
                      <ListItem
                        key={item.id}
                        secondaryAction={
                          <Box component="span">
                            <IconButton
                              size="small"
                              aria-label="Editar"
                              onClick={() => openEditQuickReply(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="Excluir"
                              onClick={() => handleDeleteQuickReply(item.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={item.mensagem || "(sem texto)"}
                          primaryTypographyProps={{ variant: "body2", sx: { whiteSpace: "pre-wrap", wordBreak: "break-word" } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
              {/* Diálogo de confirmação de exclusão */}
              <Dialog open={Boolean(deleteConfirmId)} onClose={() => setDeleteConfirmId(null)}>
                <DialogTitle>Excluir resposta pré-definida?</DialogTitle>
                <DialogContent>
                  <Typography variant="body2">Esta ação não pode ser desfeita.</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDeleteConfirmId(null)}>Cancelar</Button>
                  <Button color="error" variant="contained" onClick={confirmDeleteQuickReply}>
                    Excluir
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>
          )}
        </Grid>

        {/* Diálogo Adicionar/Editar resposta pré-definida */}
        <Dialog open={quickReplyDialogOpen} onClose={closeQuickReplyDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingQuickReply ? "Editar resposta pré-definida" : "Adicionar resposta pré-definida"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Mensagem"
              fullWidth
              multiline
              minRows={3}
              value={quickReplyMensagem}
              onChange={(e) => setQuickReplyMensagem(e.target.value)}
              placeholder="Texto que será exibido como sugestão no chat"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeQuickReplyDialog}>Cancelar</Button>
            <Button onClick={saveQuickReply} variant="contained" disabled={!(quickReplyMensagem || "").trim()}>
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Ativo/Inativo */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Box display="flex" justifyContent="flex-end">
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.status) === 1}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))
                  }
                />
              }
              label={Number(formData.status) === 1 ? "Ativo" : "Inativo"}
            />
          </Box>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddTypeForm;
